import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import BoltTable from "../../../../../../Components/BoltTable/BoltTable";
import BoltButton from "../../../../../../Components/UI/Buttons/BoltButton";
import Confirm from "../../../../../../Components/Popups/Confirm";
import {
  fetchReferralStatistics,
  fetchReferralRewards,
  checkoutReferralBalance,
} from "../../../../../../services/referral.services";
import StatusIcons from "../../../../../../Components/StatusIcons/StatusIcons";
import { convertTime } from "../../../../../../helpers/helpers";
import {
  formatMarkup,
  toWithdrawableAmount,
} from "../../../../../../helpers/referral";
import { updateUser } from "../../../../../../store/userSlice";
import { useTranslation } from "react-i18next";
import { useFormat } from "../../../../../../i18n/format";

import { MdOutlineAccountBalanceWallet, MdPayments } from "react-icons/md";

// Cash-out is paused while the payout flow is finished. Until this is true,
// Checkout Balance shows "Coming soon" and never calls the backend.
const CHECKOUT_AVAILABLE = false;

const ReferralTransactions = ({ setSnack }) => {
  const { t } = useTranslation("finances");
  const { formatMoney } = useFormat();
  // "$48.26" / "48,26 $", without thousands separators as before.
  const money = (value) => formatMoney(value, "CAD", { useGrouping: false });
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user);
  const [rewardsData, setRewardsData] = useState({ data: [] });
  const [markupTypes, setMarkupTypes] = useState({});
  const [confirmCheckout, setConfirmCheckout] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);
  const [refresh, setRefresh] = useState(false);

  const referralBalance = toWithdrawableAmount(user?.referral_balance);

  const notify = (text, severity) =>
    setSnack?.((s) => ({ ...s, open: true, text, severity }));

  // Reward rows don't say whether their referral is a percentage or a flat
  // amount, so look that up to label the Reward column. The statistics
  // endpoint returns all of the owner's referrals, unpaginated.
  useEffect(() => {
    const loadMarkupTypes = async () => {
      const res = await fetchReferralStatistics();
      const types = {};
      (res?.data?.result?.referrals ?? []).forEach((referral) => {
        types[referral.id] = referral.markup_type;
      });
      setMarkupTypes(types);
    };
    loadMarkupTypes();
  }, []);

  const loadRewards = async (page, perPage, filter) => {
    const res = await fetchReferralRewards(page, perPage, filter);
    if (res?.error) {
      notify(
        res?.error?.response?.data?.message ??
          t("referral.loadFailed"),
        "error",
      );
      return;
    }
    setRewardsData(res?.data?.result ?? { data: [] });
  };

  const handleCheckout = async () => {
    setConfirmCheckout(false);
    if (!CHECKOUT_AVAILABLE) return;
    setCheckingOut(true);
    const res = await checkoutReferralBalance({ balance: referralBalance });
    setCheckingOut(false);
    if (res?.error) {
      notify(
        res?.error?.response?.data?.message ?? t("referral.checkoutFailed"),
        "error",
      );
      return;
    }
    // Show the new balance now instead of waiting for the next /me refresh.
    const newBalance = res?.data?.result?.referral_balance;
    if (newBalance !== undefined && newBalance !== null) {
      dispatch(updateUser({ referral_balance: newBalance }));
      try {
        const stored = JSON.parse(localStorage.getItem("user"));
        if (stored) {
          localStorage.setItem(
            "user",
            JSON.stringify({ ...stored, referral_balance: newBalance }),
          );
        }
      } catch {
        // private mode — the Redux copy still holds for this session
      }
    }
    notify(res?.data?.message ?? t("referral.checkoutSubmitted"), "success");
    setRefresh((r) => !r);
  };

  const columns = [
    {
      id: 0,
      label: "#",
      field_name: "id",
      render: (row) => <>{row?.id ?? "-"}</>,
      width: "200px",
    },
    {
      id: 1,
      label: t("columns.referredUser"),
      field_name: "referee",
      render: (row) => <>{row?.referee?.username ?? "-"}</>,
      width: "200px",
    },
    {
      id: 2,
      label: t("columns.reward"),
      field_name: "markup_snapshot",
      render: (row) => {
        if (!row?.markup_snapshot) return <>-</>;
        const markupType =
          row?.referral?.markup_type ?? markupTypes[row?.referral_id];
        // Until the type is known, show the bare number rather than guess "%".
        return (
          <>
            {markupType
              ? formatMarkup(row.markup_snapshot, markupType)
              : Number(row.markup_snapshot)}
          </>
        );
      },
      width: "200px",
    },
    {
      id: 3,
      label: t("columns.amount"),
      field_name: "amount",
      render: (row) => <>{money(Number(row?.amount ?? 0))}</>,
      width: "200px",
    },
    {
      id: 4,
      label: t("columns.status"),
      field_name: "status",
      render: (row) => <StatusIcons status={row?.status} noBg={true} />,
      width: "200px",
    },
    {
      id: 5,
      label: t("columns.createdAt"),
      field_name: "created_at",
      render: (row) => <>{convertTime(row?.created_at)}</>,
      width: "200px",
    },
  ];

  return (
    <div
      className="dispatcher__screen"
      style={{ maxWidth: "100%", padding: "0px 5px" }}
    >
      <Confirm
        open={confirmCheckout}
        setOpen={setConfirmCheckout}
        handleConfirm={handleCheckout}
        text={t("referral.checkoutConfirm", { amount: money(referralBalance) })}
      />

      <div className="custom__table_size1 actionbtn__needtobe_fixwidth">
        <BoltTable
          tableTitle={t("referral.tableTitle")}
          tableId="referralTransactionsTable"
          message={t("referral.empty")}
          searchPlaceHolder={t("referral.search")}
          columns={columns}
          data={rewardsData}
          getAllData={loadRewards}
          refresh={refresh}
          headerButtons={
            <div className="flex gap-1 flex-wrap">
              <div
                className={`
                flex items-center justify-center gap-2
                px-2 min-h-[30px] min-w-fit
                rounded-lg border border-ui-border shadow-sm
                bg-ui-surface-sunken
              `}
              >
                <MdOutlineAccountBalanceWallet className="w-4 h-4 text-ui-text" />

                <span
                  className="
                  font-medium
                  text-[11px]
                  3xl:text-xs
                  4xl:text-sm
                  text-ui-text
                "
                >
                  {t("referral.availableBalance")}
                  <b> {money(referralBalance)}</b>
                </span>
              </div>
              <BoltButton
                icon={MdPayments}
                label={t("referral.checkout")}
                badge={CHECKOUT_AVAILABLE ? undefined : t("referral.comingSoon")}
                title={
                  CHECKOUT_AVAILABLE
                    ? undefined
                    : t("referral.comingSoonHint")
                }
                onClick={() => setConfirmCheckout(true)}
                variant="primary"
                size="sm"
                loading={checkingOut}
                disabled={
                  !CHECKOUT_AVAILABLE || referralBalance <= 0 || checkingOut
                }
              />
            </div>
          }
        />
      </div>
    </div>
  );
};

export default ReferralTransactions;
